import { GitCommitIcon } from '@langchain/untitled-ui-icons';

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useUpdateSessionName } from '@/Pages/Playground/PlaygroundHome/hooks/useUpdateSessionName';
import { EditableText } from '@/components/EditableText/EditableText';
import { Spinner, Tooltip } from '@/design-system';
import { useOrganizationId } from '@/hooks/useSwr';
import {
  ComparisonViewColumn,
  RunFacetField,
  SessionFeedbackDelta,
  SessionSchema,
} from '@/types/schema';
import { useClickLogger } from '@/utils/datadog/useClickLogger';
import { cn } from '@/utils/tailwind';

import { isInPlayground } from '../utils/isInPlayground';
import { AdditionalActionsHeaderButton } from './AdditionalActionsHeaderButton';
import { FeedbackDeltaComponent } from './DatasetSessionCompareFeedbackDelta';
import { DatasetSessionCompareFilter } from './DatasetSessionCompareFilter';

export const ComparisonOutputColumnHeader = ({
  col,
  session,
  sessions,
  isPairwiseUIEnabled,
  feedbackDelta,
  feedbackDeltaIsLoading,
  regressionsFilterFeedbackKey,
  projectUrl,
  setFilteringRegressionsOnSession,
  regressionsFilteringToImprovements,
  isFilteringToImprovementsPairwise,
  isReversed,
  setOpenTracingProjectId,
  filters,
  setFiltersForSession,
  runFacets,
  isRegressionTrackingUIEnabled,
  selectedBaselineSessionId,
  baselineSession,
  filteringRegressionsOnSession,
  filterToImprovements,
  setFilterParamsState,
  hubLink,
}: {
  col: ComparisonViewColumn;
  session?: SessionSchema;
  sessions?: SessionSchema[];
  isPairwiseUIEnabled: boolean;
  feedbackDelta?: SessionFeedbackDelta;
  feedbackDeltaIsLoading?: boolean;
  regressionsFilterFeedbackKey?: string;
  projectUrl?: string;
  setFilteringRegressionsOnSession?: (sessionId: string | null) => void;
  regressionsFilteringToImprovements: boolean;
  isFilteringToImprovementsPairwise: boolean;
  isReversed: boolean;
  setOpenTracingProjectId: (projectId: string) => void;
  filters: Record<string, string[]>;
  setFiltersForSession: (clauses: string[], sessionId: string) => void;
  runFacets: RunFacetField[];
  isRegressionTrackingUIEnabled: boolean;
  selectedBaselineSessionId?: string;
  baselineSession?: SessionSchema;
  filteringRegressionsOnSession: string | null;
  filterToImprovements: (improvements: boolean, sessionId: string) => void;
  setFilterParamsState?: (filters: Record<string, any>) => void;
  hubLink?: string;
}) => {
  const [, setSearchParams] = useSearchParams();
  const sessionIds = sessions?.map((s) => s.id) ?? [];
  const [isEditingName, setIsEditingName] = useState(false);

  const inPlayground = isInPlayground();

  const originalTitle = col['columnName'] ?? session?.name ?? '';
  const [title, setTitle] = useState(originalTitle);
  useEffect(() => {
    setTitle(originalTitle);
  }, [originalTitle]);

  return (
    <div className="flex max-w-full items-center justify-between gap-2 overflow-hidden">
      <ProjectLink
        disableHeaderClick={col['disableHeaderClick'] ?? false}
        projectUrl={projectUrl}
        session={session}
        setOpenTracingProjectId={setOpenTracingProjectId}
        title={title}
        setTitle={setTitle}
        onEditingNameError={() => {
          setTitle(originalTitle);
        }}
        isEditingName={isEditingName}
        setIsEditingName={setIsEditingName}
      />

      {!isEditingName && (
        <PairwiseIndicator
          sessions={sessions}
          isPairwiseUIEnabled={isPairwiseUIEnabled}
          feedbackDelta={feedbackDelta}
          feedbackDeltaIsLoading={feedbackDeltaIsLoading}
          session={session}
          sessionIds={sessionIds}
          regressionsFilterFeedbackKey={regressionsFilterFeedbackKey}
          isReversed={isReversed}
          setFilteringRegressionsOnSession={setFilteringRegressionsOnSession}
          filterToImprovements={filterToImprovements}
          filteringRegressionsOnSession={filteringRegressionsOnSession}
          isFilteringToImprovementsPairwise={isFilteringToImprovementsPairwise}
          baselineSession={baselineSession}
          isRegressionTrackingUIEnabled={isRegressionTrackingUIEnabled}
          selectedBaselineSessionId={selectedBaselineSessionId}
          regressionsFilteringToImprovements={
            regressionsFilteringToImprovements
          }
        />
      )}
      <div className="flex shrink-0 flex-row justify-end">
        {hubLink && !isEditingName && <HubLinkButton hubLink={hubLink} />}

        <div className="flex items-center justify-end">
          {session != null && !!setFilterParamsState && !isEditingName && (
            <DatasetSessionCompareFilter
              label={sessionIds.length === 1 ? undefined : session?.name}
              facets={runFacets}
              currentFilters={filters[session?.id] ?? []}
              onChange={(clauses) => {
                setFiltersForSession(clauses, session.id);
              }}
              hideClearButton={sessionIds.length === 1}
            />
          )}
        </div>

        {(sessionIds.length > 1 || inPlayground) && session && (
          <AdditionalActionsHeaderButton
            onRemoveComparison={() => {
              const newParams = sessionIds;
              newParams.splice(sessionIds.indexOf(session.id), 1);
              setSearchParams(
                (prev) => {
                  prev.set('selectedSessions', newParams.join(','));
                  return prev;
                },
                { replace: true }
              );
            }}
            onEditExperimentName={() => {
              setIsEditingName(true);
            }}
          />
        )}
      </div>
    </div>
  );
};

const ProjectLink = ({
  disableHeaderClick,
  projectUrl,
  session,
  setOpenTracingProjectId,
  title,
  setTitle,
  onEditingNameError,
  isEditingName,
  setIsEditingName,
}: {
  disableHeaderClick: boolean;
  projectUrl?: string;
  title?: string | JSX.Element;
  setTitle?: (title: string) => void;
  session?: SessionSchema;
  setOpenTracingProjectId: (string) => void;
  onEditingNameError?: () => void;
  isEditingName: boolean;
  setIsEditingName: (isEditingName: boolean) => void;
}) => {
  const logClick = useClickLogger();
  const organizationId = useOrganizationId();
  const updateSession = useUpdateSessionName(session?.id ?? '', () => {
    onEditingNameError?.();
  });

  const titleContent =
    typeof title === 'string' &&
    organizationId &&
    session?.id &&
    setTitle &&
    isEditingName ? (
      <EditableText
        value={title}
        onEdit={setTitle}
        onSubmit={async (value) => {
          await updateSession({ json: { name: value } });
        }}
        isEditing={isEditingName}
        setIsEditing={setIsEditingName}
        className="cursor-pointer truncate text-sm"
      />
    ) : (
      <span className="normal-case">{title}</span>
    );

  return (
    <Tooltip
      title={typeof title === 'string' && !isEditingName ? title : undefined}
      side="top"
    >
      {session && projectUrl && !disableHeaderClick ? (
        <div className="flex min-w-0 grow items-center gap-2 overflow-hidden">
          <div className="truncate">{titleContent}</div>
          <button
            type="button"
            className="rounded-md p-2 hover:bg-secondary-hover active:bg-secondary"
            onClick={() => {
              if (!isEditingName) {
                logClick('table_header_session_link');
                setOpenTracingProjectId(session.id);
              }
            }}
          >
            {!isEditingName && <GitCommitIcon className="size-4" />}
          </button>
        </div>
      ) : (
        <div className="flex min-w-0 items-center gap-2 overflow-hidden break-words rounded-md">
          {titleContent}
        </div>
      )}
    </Tooltip>
  );
};

const PairwiseIndicator = ({
  sessions,
  isPairwiseUIEnabled,
  feedbackDelta,
  feedbackDeltaIsLoading,
  session,
  sessionIds,
  regressionsFilterFeedbackKey,
  isReversed,
  setFilteringRegressionsOnSession,
  filterToImprovements,
  filteringRegressionsOnSession,
  isFilteringToImprovementsPairwise,
  baselineSession,
  isRegressionTrackingUIEnabled,
  selectedBaselineSessionId,
  regressionsFilteringToImprovements,
}: {
  sessions?: SessionSchema[];
  isPairwiseUIEnabled: boolean;
  feedbackDelta?: SessionFeedbackDelta;
  feedbackDeltaIsLoading?: boolean;
  session?: SessionSchema;
  sessionIds: string[];
  regressionsFilterFeedbackKey?: string;
  isReversed: boolean;
  setFilteringRegressionsOnSession?: (sessionId: string | null) => void;
  filterToImprovements: (improvements: boolean, sessionId: string) => void;
  filteringRegressionsOnSession: string | null;
  isFilteringToImprovementsPairwise: boolean;
  baselineSession?: SessionSchema;
  isRegressionTrackingUIEnabled: boolean;
  selectedBaselineSessionId?: string;
  regressionsFilteringToImprovements: boolean;
}) => {
  if (feedbackDeltaIsLoading) {
    return (
      <div className="flex items-center">
        <Spinner />
      </div>
    );
  }
  return isPairwiseUIEnabled ? (
    feedbackDelta &&
    session &&
    feedbackDelta.feedback_deltas?.[sessionIds[1]] &&
    regressionsFilterFeedbackKey ? (
      <FeedbackDeltaComponent
        delta={feedbackDelta.feedback_deltas[sessionIds[1]]}
        isReversed={isReversed}
        filterToImprovements={(improvements) => {
          if (
            !!filteringRegressionsOnSession &&
            improvements === isFilteringToImprovementsPairwise
          ) {
            setFilteringRegressionsOnSession?.(null);
          } else {
            filterToImprovements(
              isReversed ? !improvements : improvements,
              sessionIds[1]
            );
          }
        }}
        isFiltering={!!filteringRegressionsOnSession}
        isFilteringImprovements={isFilteringToImprovementsPairwise}
        isComparativeView
        expandedView
        feedbackKey={regressionsFilterFeedbackKey}
        otherExperimentName={
          sessions?.find((s) =>
            isReversed ? s.id === sessionIds[1] : s.id === sessionIds[0]
          )?.name
        }
      />
    ) : (
      <Tooltip
        side="top"
        title={`Pairwise experiment does not contain ${regressionsFilterFeedbackKey?.replace(
          /\b\w/g,
          (char) => char.toUpperCase()
        )}`}
      >
        <div className="flex items-center text-sm font-medium leading-[18px] text-ls-gray-200">
          <span className="line-clamp-1 whitespace-normal break-words break-all">
            Experiment does not contain{' '}
            {regressionsFilterFeedbackKey?.replace(/\b\w/g, (char) =>
              char.toUpperCase()
            )}
          </span>
        </div>
      </Tooltip>
    )
  ) : (
    isRegressionTrackingUIEnabled && (
      <div className="flex items-center">
        {feedbackDelta &&
        selectedBaselineSessionId &&
        session &&
        feedbackDelta.feedback_deltas?.[session.id] &&
        regressionsFilterFeedbackKey ? (
          <FeedbackDeltaComponent
            delta={feedbackDelta.feedback_deltas[session.id]}
            filterToImprovements={(improvements) => {
              if (
                filteringRegressionsOnSession === session.id &&
                improvements === regressionsFilteringToImprovements
              ) {
                setFilteringRegressionsOnSession?.(null);
              } else {
                filterToImprovements(improvements, session.id);
              }
            }}
            isFiltering={filteringRegressionsOnSession === session.id}
            isFilteringImprovements={regressionsFilteringToImprovements}
            expandedView
            isComparativeView={false}
          />
        ) : session && session.id === selectedBaselineSessionId ? (
          <div className="flex items-center rounded-full border border-black px-2 py-0.5 text-xs font-medium leading-[18px] dark:border-white">
            Baseline
          </div>
        ) : regressionsFilterFeedbackKey ? (
          <Tooltip
            side="top"
            title={`${
              baselineSession?.feedback_stats?.[regressionsFilterFeedbackKey]
                ? 'Experiment'
                : 'Baseline'
            } does not contain ${regressionsFilterFeedbackKey.replace(
              /\b\w/g,
              (char) => char.toUpperCase()
            )}`}
          >
            <div className="flex items-center text-sm font-medium leading-[18px] text-ls-gray-200">
              <span className="line-clamp-1 whitespace-normal break-words break-all">
                {baselineSession?.feedback_stats?.[regressionsFilterFeedbackKey]
                  ? 'Experiment'
                  : 'Baseline'}{' '}
                does not contain{' '}
                {regressionsFilterFeedbackKey.replace(/\b\w/g, (char) =>
                  char.toUpperCase()
                )}
              </span>
            </div>
          </Tooltip>
        ) : (
          <div className="flex items-center text-sm font-medium leading-[18px] text-ls-gray-200">
            <span className="line-clamp-1 whitespace-normal break-words break-all">
              Experiment does not contain{' '}
              {regressionsFilterFeedbackKey?.replace(/\b\w/g, (char) =>
                char.toUpperCase()
              )}
            </span>
          </div>
        )}
      </div>
    )
  );
};

const HubLinkButton = ({ hubLink }: { hubLink: string }) => {
  const logClick = useClickLogger();
  return (
    <Tooltip title={'View commit'} side="top">
      <Link
        to={hubLink}
        onClick={() => {
          logClick('table_header_hub_link');
        }}
      >
        <div
          className={cn(
            'flex min-h-[25px] w-full cursor-pointer items-center gap-1 rounded border border-transparent p-1 py-0.5 hover:bg-secondary-hover active:bg-secondary ui-open:bg-secondary'
          )}
        >
          <GitCommitIcon className="size-4" />
        </div>
      </Link>
    </Tooltip>
  );
};
